import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConversations } from '@/hooks/useConversations';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface MessageButtonProps {
  userId: string;
  userName?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  showIcon?: boolean;
  children?: React.ReactNode;
}

/**
 * Reusable button component to start a conversation with a user
 * Can be used on profile pages, sequence details, display pages, etc.
 */
export function MessageButton({
  userId,
  userName = 'this user',
  variant = 'default',
  size = 'default',
  className = '',
  showIcon = true,
  children,
}: MessageButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { startConversation } = useConversations();
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if user is logged in
    if (!user) {
      toast.error('Please log in to send messages', {
        description: 'You need to be logged in to message other users',
      });
      navigate('/auth');
      return;
    }

    // Prevent messaging yourself
    if (user.id === userId) {
      toast.error('Cannot message yourself');
      return;
    }

    try {
      setLoading(true);

      // Create or get existing conversation
      const conversation = await startConversation(userId);

      // Navigate to messages page
      navigate('/messages', {
        state: { selectedConversationId: conversation.id },
      });

      toast.success(`Opening conversation with ${userName}`);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      toast.error('Failed to start conversation', {
        description: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't show button if trying to message yourself
  if (user && user.id === userId) {
    return null;
  }

  return (
    <Button
      onClick={handleClick}
      disabled={loading}
      variant={variant}
      size={size}
      className={className}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {size !== 'icon' && <span className="ml-2">Loading...</span>}
        </>
      ) : (
        <>
          {showIcon && <MessageSquare className="h-4 w-4" />}
          {size !== 'icon' && (
            <span className={showIcon ? 'ml-2' : ''}>
              {children || 'Message'}
            </span>
          )}
        </>
      )}
    </Button>
  );
}
